import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import EventIcon from "@mui/icons-material/Event";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import SearchIcon from "@mui/icons-material/Search";
import BookmakersFilter from "./BookmakersFilter";
import { DarkModeContext } from "../contexts/DarkModeContext";
import { getSportsIcon, sportsIcons } from "../constants";

const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

const SettingsSection = ({
  selectedSport,
  handleSportChange,
  searchInput,
  setSearchInput,
  selectedDate,
  setSelectedDate,
  daysFilter,
  setDaysFilter,
  hoursFilter,
  setHoursFilter,
}) => {
  const [internalSearchInput, setInternalSearchInput] = useState(searchInput);

  // Debounce handler
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(internalSearchInput);
    }, 300); // Debounce time in milliseconds

    return () => {
      clearTimeout(handler);
    };
  }, [internalSearchInput, setSearchInput]);

  const { darkMode } = useContext(DarkModeContext);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        paddingX: "16px",
        alignItems: "stretch",
        mt: { xs: 4, lg: 0 },
        width: "100%",
        backgroundColor: darkMode ? "secondary.main" : "inherit",
        color: darkMode ? "text.primary" : "inherit",
        borderRadius: "8px",
        padding: "16px",
        boxShadow: darkMode ? "0px 4px 10px rgba(0, 0, 0, 0.1)" : "none",
        order: { xs: 2, lg: 1 }
      }}
    >
      <Typography variant="h6" sx={{ marginBottom: "16px" }}>
        Filters & Search
      </Typography>

      <TextField
        variant="outlined"
        placeholder="Search Events"
        value={internalSearchInput}
        onChange={(e) => setInternalSearchInput(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          sx: {
            backgroundColor: darkMode ? "background.paper" : "inherit",
            color: darkMode ? "text.primary" : "inherit",
          },
        }}
      />

      <FormControl fullWidth>
        <InputLabel
          id="sport-select-label"
          sx={{
            color: darkMode ? "text.primary" : "inherit",
          }}
        >
          Sport
        </InputLabel>
        <Select
          labelId="sport-select-label"
          id="sport-select"
          value={selectedSport}
          onChange={handleSportChange}
          startAdornment={
            <InputAdornment position="start">
              {getSportsIcon(selectedSport)}
            </InputAdornment>
          }
          endAdornment={
            selectedSport && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear sport"
                  onClick={() => handleSportChange(null)}
                  edge="end"
                  size="small"
                  sx={{ marginRight: "8px" }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }
          label="Sport"
          sx={{
            backgroundColor: darkMode ? "background.paper" : "inherit",
            color: darkMode ? "text.primary" : "inherit",
          }}
        >
          {Object.keys(sportsIcons).map((sport) => (
            <MenuItem
              key={sport}
              value={sport}
              sx={{
                color: darkMode ? "text.primary" : "inherit",
              }}
            >
              {sport}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <BookmakersFilter darkMode={darkMode} />

      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          label="Date"
          value={selectedDate}
          onChange={(newValue) => setSelectedDate(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EventIcon />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: darkMode ? "background.paper" : "inherit",
                  color: darkMode ? "text.primary" : "inherit",
                },
              }}
            />
          )}
        />
      </LocalizationProvider>

      <Box
        sx={{
          display: "flex",
          gap: "16px",
        }}
      >
        <TextField
          label="Days"
          type="number"
          value={daysFilter}
          onChange={(e) => setDaysFilter(e.target.value)}
          InputProps={{
            sx: {
              backgroundColor: darkMode ? "background.paper" : "inherit",
              color: darkMode ? "text.primary" : "inherit",
            },
          }}
          fullWidth
        />
        <TextField
          label="Hours"
          type="number"
          value={hoursFilter}
          onChange={(e) => setHoursFilter(e.target.value)}
          InputProps={{
            sx: {
              backgroundColor: darkMode ? "background.paper" : "inherit",
              color: darkMode ? "text.primary" : "inherit",
            },
          }}
          fullWidth
        />
      </Box>
    </Box>
  );
};

export default SettingsSection;
